/**
 * Funnels List Page
 * Displays saved funnels with step flow previews and quick actions
 */

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { PageHeader, EmptyState } from "@/components/shared/layout";
import DemoSiteBanner from "@/components/shared/DemoSiteBanner";
import { FeatureGate } from "@/components/shared/FeatureGate";
import { logFunnelEvent, reportFunnelFailure } from "@/lib/observability/funnels";
import { useSavedFunnels, useSaveFunnel, useDeleteFunnel } from "@/hooks/useAnalytics";
import { usePlanUsage, useInvalidatePlanUsage } from "@/hooks/usePlanUsage";
import { CreateOrUpgradeButton } from "@/components/shared/plan-usage";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";
import { getPlanDisplayName } from "@/lib/constants/pricing";
import { generateSlugId } from "@/lib/routing/slug-id";
import AnalyticsTabBar from "@/components/dashboard/AnalyticsTabBar";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  ArrowRight01Icon,
  Delete01Icon,
  FileAttachmentIcon,
  FileDownloadIcon,
  Layers01Icon,
  MoreHorizontalIcon,
  PlusSignIcon,
} from "@hugeicons/core-free-icons"

interface DashboardContext {
  dashboardData: any;
  isLoading: boolean;
  propertyId?: string;
  property?: any;
}

interface FunnelStep {
  id: string;
  name: string;
  url: string;
}

export default function Funnels() {
  const { propertyId, property } = useOutletContext<DashboardContext>();
  const workspaceContext = useWorkspaceSafe();
  const isAggregateMode = !!workspaceContext?.isAggregateMode;
  const navigate = useNavigate();
  const location = useLocation();
  const isShareMode = location.pathname.startsWith('/s/');

  const { data: savedFunnels, isLoading: funnelsLoading } = useSavedFunnels({ propertyId });
  const saveFunnel = useSaveFunnel({ propertyId });
  const deleteFunnel = useDeleteFunnel({ propertyId });
  const invalidatePlanUsage = useInvalidatePlanUsage();
  const [funnelToDelete, setFunnelToDelete] = useState<any | null>(null);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newFunnel, setNewFunnel] = useState({
    name: "",
    description: "",
    steps: [
      { id: "1", name: "Landing Page", url: "/" },
      { id: "2", name: "Product Page", url: "/product" },
      { id: "3", name: "Checkout", url: "/checkout" },
      { id: "4", name: "Thank You", url: "/thank-you" },
    ],
  });

  // Shared plan-usage hook. Note the create/upgrade UI below is
  // gated on `!isShareMode`, and this page is only ever mounted under /s/*
  // (magic-link share routes) — so it never actually renders in practice.
  // Kept wired to the shared source of truth anyway rather than the old
  // local `features.funnelLimit` math, in case that routing changes.
  const { funnels: funnelsUsage } = usePlanUsage({
    propertyId,
    fallbackCounts: { funnels: savedFunnels?.funnels?.length },
  });
  const { openPricingModal, currentPlan, planName } = useSubscription();
  const displayPlanName = getPlanDisplayName(currentPlan, planName);
  const funnels = savedFunnels?.funnels || [];
  const funnelLimitReached = !funnelsUsage.canAdd;

  const handleCreateFunnel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (funnelLimitReached) {
      openPricingModal({ reason: "funnel_limit" });
      return;
    }
    try {
      await saveFunnel.mutateAsync(newFunnel);
      logFunnelEvent("funnel_saved", { propertyId, domain: property?.domain }, {
        funnelName: newFunnel.name,
        stepCount: newFunnel.steps.length,
      });
      invalidatePlanUsage();
      toast.success("Funnel created successfully!", {
        description: `"${newFunnel.name}" is now saved.`,
      });
      setShowCreateForm(false);
      setNewFunnel({
        name: "",
        description: "",
        steps: [
          { id: "1", name: "Landing Page", url: "/" },
          { id: "2", name: "Product Page", url: "/product" },
          { id: "3", name: "Checkout", url: "/checkout" },
          { id: "4", name: "Thank You", url: "/thank-you" },
        ],
      });
    } catch (error: any) {
      reportFunnelFailure("save", error, { propertyId, domain: property?.domain }, {
        funnelName: newFunnel.name,
        stepCount: newFunnel.steps.length,
      });
      toast.error("Failed to create funnel", {
        description: error?.message || "Please try again.",
      });
    }
  };

  const addFunnelStep = () => {
    const newStep: FunnelStep = {
      id: Math.random().toString(36).substr(2, 9),
      name: `Step ${newFunnel.steps.length + 1}`,
      url: "",
    };
    setNewFunnel((prev) => ({
      ...prev,
      steps: [...prev.steps, newStep],
    }));
  };

  const removeFunnelStep = (stepId: string) => {
    if (newFunnel.steps.length <= 2) return;
    setNewFunnel((prev) => ({
      ...prev,
      steps: prev.steps.filter((step) => step.id !== stepId),
    }));
  };

  const updateFunnelStep = (stepId: string, field: string, value: string) => {
    setNewFunnel((prev) => ({
      ...prev,
      steps: prev.steps.map((step) =>
        step.id === stepId ? { ...step, [field]: value } : step
      ),
    }));
  };

  const handleViewFunnel = (funnel: any) => {
    const slugId = generateSlugId(funnel.name, funnel.funnel_id || funnel.funnelId || funnel.id);
    const currentPath = window.location.pathname;
    const basePath = currentPath.substring(0, currentPath.lastIndexOf("/"));
    navigate(`${basePath}/funnels/${slugId}${window.location.search}`);
  };

  const handleDeleteFunnel = async () => {
    if (!funnelToDelete) return;
    try {
      const funnelId = funnelToDelete.funnel_id || funnelToDelete.funnelId || funnelToDelete.id;
      await deleteFunnel.mutateAsync(funnelId);
      invalidatePlanUsage();
      logFunnelEvent("funnel_deleted", { propertyId, domain: property?.domain }, {
        funnelId,
        funnelName: funnelToDelete.name,
      });
      invalidatePlanUsage();
      toast.success(`"${funnelToDelete.name}" has been deleted`);
      setFunnelToDelete(null);
    } catch (error) {
      reportFunnelFailure("delete", error, { propertyId, domain: property?.domain }, {
        funnelName: funnelToDelete.name,
      });
      toast.error("Failed to delete funnel");
    }
  };

  // Navigate to funnel studio to edit
  const handleEditFunnel = (funnel: any) => {
    const currentPath = window.location.pathname;
    const basePath = currentPath.substring(0, currentPath.lastIndexOf("/"));
    const siteParam = new URLSearchParams(window.location.search).get("site");
    const siteQuery = siteParam ? `&site=${siteParam}` : "";
    navigate(`${basePath}/funnels?funnel=${funnel.funnel_id || funnel.funnelId || funnel.id}${siteQuery}`);
  };

  if (funnelsLoading) {
    return (
      <TooltipProvider>
        <div className="w-full">
          <div className="flex flex-col gap-6">
            <PageHeader
              title="Funnels"
              description="Track conversion paths and identify drop-off points"
            />
            <div className="grid gap-4">
              {[...Array(3)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <div className="h-32 bg-muted/30 animate-pulse rounded-lg" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <FeatureGate feature="funnels">
    <TooltipProvider>
    <div className="w-full">
    <div className="flex flex-col gap-6">
      {isShareMode && <AnalyticsTabBar basePath={location.pathname.replace(/\/funnels.*$/, '')} />}
      {/* Header */}
      <PageHeader
        title="Funnels"
        description="Track conversion paths and identify drop-off points"
        actions={
          !isShareMode ? (
            <div className="flex items-center gap-2 flex-wrap">
              <CreateOrUpgradeButton
                atLimit={funnelLimitReached}
                onClick={() => setShowCreateForm(true)}
                className="whitespace-nowrap"
                noun="funnels"
                limit={funnelsUsage.limit}
                planName={displayPlanName}
              >
                New funnel
              </CreateOrUpgradeButton>
            </div>
          ) : undefined
        }
      />

      {/* Demo Site Banner */}
      <DemoSiteBanner propertyId={propertyId} />

      {/* Create Form */}
      <AnimatePresence>
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="overflow-hidden">
              <CardHeader className="border-b pb-3">
                <CardTitle className="text-sm font-medium">
                  Create new funnel
                </CardTitle>
                <CardDescription className="text-xs">
                  Define the steps in your conversion funnel
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <form onSubmit={handleCreateFunnel} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="name"
                        className="metric-label"
                      >
                        Funnel name
                      </Label>
                      <Input
                        id="name"
                        value={newFunnel.name}
                        onChange={(e) =>
                          setNewFunnel({ ...newFunnel, name: e.target.value })
                        }
                        placeholder="e.g., Signup Flow"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label
                        htmlFor="description"
                        className="metric-label"
                      >
                        Description (Optional)
                      </Label>
                      <Input
                        id="description"
                        value={newFunnel.description}
                        onChange={(e) =>
                          setNewFunnel({
                            ...newFunnel,
                            description: e.target.value,
                          })
                        }
                        placeholder="Brief description of the funnel"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label className="metric-label">
                      Funnel steps
                    </Label>
                    <div className="space-y-2">
                      {newFunnel.steps.map((step, index) => (
                        <motion.div
                          key={step.id}
                          className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/30"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <div className="w-8 h-8 rounded-full bg-foreground flex items-center justify-center text-white text-sm font-semibold shrink-0">
                            {index + 1}
                          </div>
                          <Input
                            placeholder="Step name"
                            value={step.name}
                            onChange={(e) =>
                              updateFunnelStep(step.id, "name", e.target.value)
                            }
                            className="flex-1"
                          />
                          <Input
                            placeholder="/page-url"
                            value={step.url}
                            onChange={(e) =>
                              updateFunnelStep(step.id, "url", e.target.value)
                            }
                            className="flex-1"
                          />
                          {newFunnel.steps.length > 2 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeFunnelStep(step.id)}
                              className="text-muted-foreground hover:text-destructive"
                            >
                              <HugeiconsIcon icon={Delete01Icon} size={16} />
                            </Button>
                          )}
                        </motion.div>
                      ))}
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={addFunnelStep}
                      className="w-full gap-2"
                    >
                      <HugeiconsIcon icon={PlusSignIcon} size={16} />
                      Add step
                    </Button>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button
                      type="submit"
                      disabled={!newFunnel.name || saveFunnel.isPending}
                      className="gap-2"
                    >
                      <HugeiconsIcon icon={FileDownloadIcon} size={16} />
                      {saveFunnel.isPending ? "Saving..." : "Create funnel"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCreateForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Start - shown when no funnels exist (not in share mode) */}
      {funnels.length === 0 && !showCreateForm && !isShareMode && (
        <Card className="border-border/30 bg-muted/20">
          <CardHeader className="border-b pb-3">
            <CardTitle className="text-sm font-semibold text-foreground">
              Quick start
            </CardTitle>
          </CardHeader>
          <CardContent>
            <button
              onClick={() => setShowCreateForm(true)}
              className="w-full p-4 text-left rounded-lg border border-dashed border-input hover:border-foreground/40 hover:bg-muted/5 transition-all group"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0 group-hover:bg-muted/80 transition-colors">
                  <HugeiconsIcon icon={Layers01Icon} size={16} className="text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium text-primary text-sm group-hover:underline underline-offset-2">
                    Create your first funnel
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Define steps to track user conversion paths
                  </p>
                </div>
              </div>
            </button>
          </CardContent>
        </Card>
      )}

      {/* Funnels List */}
      <div className="space-y-3">
        {funnels.length === 0 ? (
          <Card className="border-border/30">
            <CardContent className="p-0">
              <EmptyState
                title="No funnels yet"
                description="Create your first funnel to track user conversion paths and identify where users drop off."
                action={
                  !isShareMode ? (
                    <CreateOrUpgradeButton
                      atLimit={funnelLimitReached}
                      onClick={() => setShowCreateForm(true)}
                      noun="funnels"
                      limit={funnelsUsage.limit}
                      planName={displayPlanName}
                    >
                      Create first funnel
                    </CreateOrUpgradeButton>
                  ) : undefined
                }
                secondaryAction={
                  <Button variant="outline" asChild>
                    <a href="https://docs.humblytics.com" target="_blank" rel="noopener noreferrer">
                      <HugeiconsIcon icon={FileAttachmentIcon} size={14} className="mr-1.5" />
                      How funnels work
                    </a>
                  </Button>
                }
              />
            </CardContent>
          </Card>
        ) : (
          funnels.map((funnel: any, index: number) => {
            const funnelSteps = funnel.steps || [];

            return (
              <motion.div
                key={funnel.funnel_id || funnel.funnelId || funnel.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 + index * 0.04 }}
              >
                <Card
                  className="group hover:shadow-md transition-all cursor-pointer border-border/30 hover:border-primary/30"
                  onClick={() => handleViewFunnel(funnel)}
                >
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-4">
                      {/* Left: Funnel info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <h3 className="font-semibold text-sm truncate max-w-[280px] group-hover:text-foreground transition-colors">
                            {funnel.name}
                          </h3>
                          <Badge variant="outline" className="text-[11px] shrink-0">
                            {funnelSteps.length} steps
                          </Badge>
                          {isAggregateMode && funnel._propertyDomain && (
                            <Badge variant="outline" className="text-[11px] shrink-0 bg-muted/30">
                              {funnel._propertyDomain}
                            </Badge>
                          )}
                        </div>

                        {funnel.description && (
                          <p className="text-xs text-muted-foreground mb-3 line-clamp-1">
                            {funnel.description}
                          </p>
                        )}

                        {/* Step flow preview */}
                        {funnelSteps.length > 0 && (
                          <div className="flex items-center gap-1 flex-wrap">
                            {funnelSteps.slice(0, 5).map((s: any, idx: number) => (
                              <div key={idx} className="flex items-center gap-1">
                                <span className="inline-flex items-center gap-1 text-[11px] font-mono text-muted-foreground bg-muted/40 px-2 py-0.5 rounded truncate max-w-[140px]">
                                  <span className="w-4 h-4 rounded-full bg-muted text-foreground text-[11px] font-semibold flex items-center justify-center shrink-0">
                                    {idx + 1}
                                  </span>
                                  {s.url || s.path || s.name}
                                </span>
                                {idx < Math.min(funnelSteps.length, 5) - 1 && (
                                  <HugeiconsIcon icon={ArrowRight01Icon} size={12} className="text-muted-foreground/30 shrink-0" />
                                )}
                              </div>
                            ))}
                            {funnelSteps.length > 5 && (
                              <span className="text-[11px] text-muted-foreground ml-1">+{funnelSteps.length - 5} more</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Right: Actions */}
                      <div className="flex items-center gap-2 shrink-0">
                        {!isShareMode && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs font-medium gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditFunnel(funnel);
                            }}
                          >
                            Edit
                          </Button>
                        )}

                        <DropdownMenu>
                          <DropdownMenuTrigger
                            asChild
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <HugeiconsIcon icon={MoreHorizontalIcon} size={16} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewFunnel(funnel);
                              }}
                            >
                              View details
                            </DropdownMenuItem>
                            {!isShareMode && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFunnelToDelete(funnel);
                                  }}
                                >
                                  Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>

                        <HugeiconsIcon icon={ArrowRight01Icon} size={18} className="text-muted-foreground/40 group-hover:text-foreground transition-colors" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!funnelToDelete}
        onOpenChange={() => setFunnelToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete funnel</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{funnelToDelete?.name}"? This action cannot be
              undone and all associated data will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFunnel}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </div>
    </TooltipProvider>
    </FeatureGate>
  );
}
